import type { Locale } from './i18n.js';

/**
 * Реестр брендов для CRM.
 *
 * Система разворачивается экземплярами на клиента: код один, образ один,
 * а различия живут в `.env` экземпляра — переменная `BRAND_PROFILE`. Сайт
 * умел это с самого начала (apps/site-westauto/src/content/brands/), CRM —
 * нет, и клиент видел чужое имя ровно там, где складывается первое
 * впечатление: на экране входа.
 *
 * Реестр лежит в shared, а не в API или в приложении, потому что нужен обоим:
 * API отдаёт запись наружу (`GET /api/brand`), приложение её показывает.
 * Если бы запись жила в API, у приложения не было бы её типа; если бы в
 * приложении — API нечего было бы отдавать.
 *
 * Полей здесь ровно столько, сколько нужно CRM. Контакты, логотипы, палитра
 * сайта и прочее богатство профиля сайта сюда не переносятся: у CRM один
 * брендированный экран и одна шапка, а лишнее поле — это поле, которое
 * однажды разойдётся с сайтом и начнёт врать.
 */

/**
 * Знак бренда в шапке и на экране входа.
 *
 * Строкой, а не компонентом: shared собирается без React и работает в том
 * числе на сервере. Сопоставление строки с картинкой — дело приложения
 * (apps/app/src/components/Icons.tsx).
 */
export type BrandMark = 'key' | 'ship';

/**
 * Палитра одного клиентского документа.
 *
 * Отдельно от палитры интерфейса (`accent`/`accentInk`/`darkPanel`) по двум
 * причинам. Первая: интерфейс подчиняется теме, которую менеджер выбрал себе,
 * а документ обязан выглядеть одинаково у всех — его снимают скриншотом и
 * отправляют клиенту. Вторая: `accent` интерфейса бывает `null` («оставить
 * цвет темы»), а у документа «цвета темы» нет вовсе — ему нужно значение
 * всегда, поэтому здесь нет ни одного `null`, кроме `logoPlate`.
 *
 * Поля жёстко обязательны ещё и затем, чтобы новый бренд нельзя было завести
 * наполовину: отсутствующий цвет документа — это не «как раньше», а чужой
 * фирменный стиль на бумаге клиента.
 *
 * ⚠️ Контраст пар проверен расчётом WCAG (AA: 4.5:1 для обычного текста,
 * 3:1 для крупного). Меняя любой цвет, пересчитать — самое опасное место
 * здесь не заголовок, а мелкая приписка цветом `inkSoft`.
 */
export interface BrandDocPalette {
  /** Фон документа. */
  readonly bg: string;

  /**
   * Фон врезок на документе — дисклеймер КП, подложка под реквизиты.
   * Отдельный цвет, а не прозрачность: снимок в PNG делается через
   * foreignObject, и полупрозрачные слои там считаются иначе, чем на экране.
   */
  readonly panel: string;

  /** Основной текст на `bg` и на `panel`. */
  readonly ink: string;

  /** Приглушённый текст: подписи, приписка о доплатах, курс. */
  readonly inkSoft: string;

  /** Разделители строк и рамки. */
  readonly line: string;

  /** Акцент итога — заливка блока с итоговой суммой. */
  readonly accent: string;

  /**
   * Текст на акценте. Отдельно от `accent` ровно по той же причине, что и
   * `accentInk` интерфейса: на красном читается белый, на жёлтом — тёмный,
   * и одним полем это не выражается.
   */
  readonly accentInk: string;

  /**
   * Цвет svg-знака у профиля без картинки логотипа.
   *
   * На тёмном фоне документа тёмный знак пропадает совсем, причём незаметно:
   * документ выглядит целым, просто без знака. Поэтому цвет знака — поле
   * профиля, а не константа в компоненте.
   */
  readonly mark: string;

  /**
   * Светлая подложка под картинку логотипа или `null` — подложка не нужна.
   *
   * Логотип приходит картинкой, и перекрасить его нельзя. У WestAuto щит и
   * надпись «USA AUTO GROUP» нарисованы тем же флотским синим, что и фон
   * документа, — без подложки от знака остаётся только красное слово.
   */
  readonly logoPlate: string | null;
}

/**
 * Палитра печатного листа — то же плюс фирменный цвет шапки и заголовков.
 *
 * Зачем отдельное поле `heading`, которого нет у палитры экрана: на тёмной
 * карточке заголовков нет вовсе, там весь текст одного цвета (`ink`). На
 * светлом листе наоборот — если шапку, название документа и подписи разделов
 * не покрасить фирменным цветом, получится просто чёрный текст на белом, то
 * есть документ без бренда. Выражать это через `mark` нельзя: `mark` — цвет
 * svg-знака, и у бренда с картинкой логотипа он к заголовкам отношения не
 * имеет.
 *
 * Поле живёт в палитре печати, а не в общей, чтобы у палитры экрана не
 * появилось значение, которое никто не читает: неиспользуемое поле через
 * месяц разойдётся с остальными и начнёт врать.
 */
export interface BrandDocPrintPalette extends BrandDocPalette {
  /** Фирменный цвет шапки, названия документа и подписей разделов. */
  readonly heading: string;
}

/**
 * Две палитры клиентских документов — по одной на способ доставки.
 *
 * Документов два, и живут они по-разному. Карточку расчёта менеджер снимает
 * скриншотом и отправляет в мессенджер: там тёмный фон — это образец
 * заказчика и его прямая просьба («чтобы на весь экран помещался, сверху
 * логотип»). Коммерческое предложение уходит клиенту файлом PDF формата A4,
 * и его печатают: тот же тёмно-синий фон на бумаге — это полный лист краски.
 *
 * Поэтому палитра не одна, а две, и обе — в профиле бренда. Разводить
 * документы цветом прямо в вёрстке нельзя: тогда третий бренд пришлось бы
 * заводить правками в css и tsx, а не одной записью здесь.
 *
 * Чего в этой паре НЕТ и быть не должно — разных чисел и разного состава.
 * Разводятся только цвета: два документа по одному расчёту обязаны читаться
 * как один комплект.
 */
export interface BrandDocPalettes {
  /** Карточка расчёта — снимок в мессенджер, фон тёмный. */
  readonly screen: BrandDocPalette;

  /** Коммерческое предложение — PDF на A4, лист светлый. */
  readonly print: BrandDocPrintPalette;
}

/**
 * Какой из двух документов красим.
 *
 * `keyof`, а не свой союз строк: добавится третья поверхность — её имя
 * появится здесь само, и забыть про него будет нельзя.
 */
export type BrandDocSurface = keyof BrandDocPalettes;

export interface CrmBrand {
  /** Значение `BRAND_PROFILE`, по которому профиль выбирается. */
  readonly id: string;

  /** Полное название — для подвала и заголовков. */
  readonly name: string;

  /**
   * Хвост названия, который вёрстка выделяет курсивом (`Авто<em>Ключ</em>`).
   *
   * Храним хвост, а не две половины: половины рано или поздно разойдутся с
   * полным именем, а хвост от него неотделим — он обязан быть его концом,
   * и `splitBrandName()` это проверяет. У односоставного имени хвоста нет —
   * `null`, а не пустая строка: «нет выделения» и «выделить ничего» это
   * разные вещи только на вид, но пустая строка прячет опечатку.
   */
  readonly nameEmphasis: string | null;

  /** Знак рядом с названием. */
  readonly mark: BrandMark;

  /**
   * Логотип картинкой для документов клиенту — путь внутри приложения,
   * относительно его базового адреса (`/app/`). `null` — логотипа нет,
   * документ рисует прежнюю пару «svg-знак + название».
   *
   * Путь относительный намеренно: shared не знает, по какому адресу
   * развёрнуто приложение, а приложение знает (`import.meta.env.BASE_URL`).
   * Абсолютный `/app/...` тихо сломался бы на экземпляре с другим префиксом.
   *
   * Только для клиентских документов. Шапка и экран входа остаются на
   * svg-знаке: он рисуется цветом темы и не мылится на любом размере.
   */
  readonly logo: string | null;

  /**
   * Акцентный цвет (`--accent`) или `null` — «оставить цвет темы».
   *
   * `null` нужен не для красоты: у АвтоКлюча акцент задан в app.css дважды —
   * свой для тёмной темы и свой для светлой. Один hex не может быть обоими,
   * и жёсткое значение здесь испортило бы светлую тему живого экземпляра.
   * Профиль, которому своя палитра не нужна, ставит `null` и получает ровно
   * то, что было до появления реестра.
   */
  readonly accent: string | null;
  /**
   * Цвет текста на акцентных кнопках или `null` — «оставить цвет темы».
   *
   * Отдельно от accent потому, что контраст зависит от самого акцента:
   * на тёплом золотом АвтоКлюча читается тёмный текст, на зелёном — белый.
   * Одним полем это не выражается, а ошибка видна сразу на каждой кнопке.
   */
  readonly accentInk: string | null;

  /** Фон тёмной панели входа (`--ink`) или `null` — «оставить цвет темы». */
  readonly darkPanel: string | null;

  /** Палитры карточки расчёта и КП — документов, которые видит клиент. */
  readonly doc: BrandDocPalettes;

  /**
   * Короткая фраза на экране входа — одна мысль, в кавычках-ёлочках.
   *
   * ⚠️ Текст экземпляра, а не общая строка интерфейса, — поэтому живёт здесь,
   * а не в словаре CRM: у каждого клиента она своя. Но интерфейс переключает
   * язык, и одноязычная фраза делала украинский вход наполовину русским,
   * причём самой заметной своей половиной.
   */
  readonly quote: Record<Locale, string>;

  /** Абзац под фразой: чем система занимается. Два-три предложения. */
  readonly intro: Record<Locale, string>;

  /**
   * Подпись в подвале экрана входа. `{year}` подставляется текущим годом —
   * см. `brandFooter()`.
   *
   * Строкой целиком, а не «названием плюс примечанием»: у следующего
   * экземпляра подпись может оказаться с другой структурой, и собирать её из
   * кусков тогда нечем.
   */
  readonly footer: Record<Locale, string>;
}

/**
 * АвтоКлюч / WestAuto — исходный экземпляр, он же поведение по умолчанию.
 *
 * Тексты перенесены с экрана входа дословно. Любая правка здесь меняет вид
 * живого сайта, поэтому править только вместе со сверкой до/после.
 */
export const westautoCrmBrand: CrmBrand = {
  id: 'westauto',
  name: 'АвтоКлюч',
  nameEmphasis: 'Ключ',
  mark: 'key',
  // Логотип WestAuto: внутри картинки уже есть название, поэтому в документе
  // она заменяет собой и знак, и надпись
  logo: 'brand/westauto-logo-256.png',
  accent: null,
  accentInk: null,
  darkPanel: null,
  /*
   * Фирменная пара WestAuto — флотский синий со щита логотипа и красный со
   * слова WEST AUTO. Те же hex, что в палитре сайта
   * (apps/site-westauto/src/content/brands/westauto.ts): документ клиенту и
   * сайт обязаны быть одного цвета, иначе у бренда два разных лица.
   *
   * Заказчик 02.11 попросил убрать кремовый фон: «в рекламке тоже красно-синие
   * цвета, а тут жёлтые — нестыковка по бренду». Прежняя кремовая палитра была
   * от АвтоКлюча и к WestAuto отношения не имела.
   */
  doc: {
    /*
     * Карточка в мессенджер — тёмно-синий лист. Значения не менялись с
     * 02.11: это образец заказчика, и карточку правим только по его просьбе.
     *
     * Контраст (WCAG): ink/bg 13,2:1 · inkSoft/bg 6,8:1 · inkSoft/panel 4,8:1 ·
     * accentInk/accent 4,7:1 — все пары выше порога AA 4,5:1.
     */
    screen: {
      bg: '#13293c',
      panel: '#1f425e',
      ink: '#eef2f5',
      inkSoft: '#9fb2c0',
      line: '#35607f',
      accent: '#e22131',
      accentInk: '#ffffff',
      mark: '#e22131',
      // Логотип WestAuto нарисован синим и красным — на синем фоне нужен белый
      // прямоугольник под ним, иначе от знака остаётся одно красное слово
      logoPlate: '#ffffff',
    },

    /*
     * КП на A4 — белый лист с тёмно-синим текстом. Тот же фирменный синий,
     * но ролями наоборот: на экране он фон, на бумаге — чернила. Так КП
     * печатается без заливки, оставаясь той же гаммы, что карточка.
     *
     * Красная плашка итога сохранена нарочно: это образец заказчика, и ровно
     * она связывает светлый лист с тёмной карточкой — на обоих документах
     * итог выглядит одинаково.
     *
     * Контраст (WCAG, посчитан): ink/bg 14,9:1 · heading/bg 10,5:1 ·
     * inkSoft/bg 6,3:1 · inkSoft/panel 5,6:1 · ink/panel 13,3:1 ·
     * accentInk/accent 4,7:1. Самая опасная пара — приглушённая приписка
     * на подложке дисклеймера, 5,6:1 при пороге 4,5:1.
     */
    print: {
      bg: '#ffffff',
      // Подложка дисклеймера — голубовато-серая, а не нейтральная: на листе
      // она единственное цветное поле и обязана быть той же гаммы
      panel: '#eef3f7',
      ink: '#13293c',
      inkSoft: '#4a6376',
      // Линии таблицы — светлый синий: фирменный цвет виден, но строки не
      // превращаются в сетку из жирных черт
      line: '#b9cddc',
      accent: '#e22131',
      accentInk: '#ffffff',
      mark: '#13293c',
      // На белом листе логотип WestAuto читается как есть — подложка под ним
      // была бы белым прямоугольником на белом, то есть лишней рамкой
      logoPlate: null,
      heading: '#1f425e',
    },
  },
  quote: {
    uk: '«Один розрахунок — від ставки на аукціоні до ціни клієнту.»',
    ru: '«Один расчёт — от ставки на аукционе до цены клиенту.»',
    en: '“One quote — from the auction bid to the client’s price.”',
  },
  intro: {
    uk:
      'Платформа розрахунку вартості доставки та розмитнення авто з Copart і IAAI під ключ. ' +
      'Тарифи, історія змін і ролі — в одному місці.',
    ru:
      'Платформа расчёта стоимости доставки и растаможки авто с Copart и IAAI под ключ. ' +
      'Тарифы, история изменений и роли — в одном месте.',
    en:
      'A turnkey platform for quoting shipping and customs on cars from Copart and IAAI. ' +
      'Tariffs, change history and roles — all in one place.',
  },
  footer: {
    uk: '© {year} АвтоКлюч · доступ лише для співробітників',
    ru: '© {year} АвтоКлюч · доступ только для сотрудников',
    en: '© {year} AvtoKlyuch · staff access only',
  },
};

/**
 * Larus Logistics — экземпляр на laruslogistics.seoshkin.tools.
 *
 * Факты взяты из профиля их сайта (apps/site-westauto/src/content/brands/
 * larus.ts): аукционы, маршрут через Клайпеду, выдача во Львове, работа с
 * 2020 года. Ничего придуманного: выдуманная цифра на экране входа подрывает
 * доверие ко всему, что система потом посчитает.
 *
 * Тексты на трёх языках: CRM переключает язык интерфейса (02.11.2026), и
 * одноязычная подпись посреди переведённого экрана выглядит недоделкой —
 * ровно тем, чем раньше выглядела украинская строка посреди русского.
 */
export const larusCrmBrand: CrmBrand = {
  id: 'larus',
  name: 'Larus Logistics',
  nameEmphasis: null,
  mark: 'ship',
  // Логотипа от клиента нет — документы рисуют знак с названием, как раньше
  logo: null,
  accent: '#16a34a',
  accentInk: '#ffffff',
  darkPanel: '#0f1b2d',
  /*
   * Своя гамма, а не синий с красным: синий с красным — фирменные цвета
   * WestAuto, и ставить их чужому бренду значит отдать клиенту документ
   * конкурента.
   *
   * Значения рабочие, но не вылизанные: Larus правим отдельным заходом, когда
   * клиент пришлёт свои. Главное здесь — что документ не остаётся без палитры.
   *
   * ⚠️ Зелёный итога — `#15803d`, а не интерфейсный `#16a34a`: белый на
   * `#16a34a` даёт 3,3:1 и проваливает AA на подписи «Итого» (15px жирный —
   * это НЕ крупный текст). На `#15803d` — 5,0:1.
   */
  doc: {
    /* Карточка. Контраст: ink/bg 14,8:1 · inkSoft/bg 7,4:1 · inkSoft/panel 6,2:1. */
    screen: {
      bg: '#0f1b2d',
      panel: '#1a2a40',
      ink: '#e8eef5',
      inkSoft: '#9aacc0',
      line: '#2b4057',
      accent: '#15803d',
      accentInk: '#ffffff',
      mark: '#22c55e',
      // Картинки логотипа у Larus нет — документ рисует svg-знак, ему подложка
      // не нужна: он красится цветом `mark`
      logoPlate: null,
    },

    /*
     * КП на A4 — светлый лист в той же гамме. Собран по тому же правилу, что
     * у WestAuto: фон белый, фирменный тёмно-синий ушёл в текст, акцент бренда
     * (зелёный) остался на плашке итога.
     *
     * ⚠️ Знак на листе — зелёный `#15803d`, а не интерфейсный `#22c55e`:
     * светло-зелёный на белом даёт 2,2:1 и знак на бумаге почти исчезает.
     *
     * Контраст (посчитан): ink/bg 17,3:1 · heading/bg 12,8:1 ·
     * inkSoft/bg 6,7:1 · inkSoft/panel 5,9:1 · accentInk/accent 5,0:1 ·
     * mark/bg 5,0:1.
     */
    print: {
      bg: '#ffffff',
      panel: '#eef2f6',
      ink: '#0f1b2d',
      inkSoft: '#4b5e73',
      line: '#c3ccd7',
      accent: '#15803d',
      accentInk: '#ffffff',
      mark: '#15803d',
      logoPlate: null,
      heading: '#1d3350',
    },
  },
  quote: {
    uk: '«Лот на аукціоні США — машина у Львові.»',
    ru: '«Лот на аукционе США — машина во Львове.»',
    en: '“A lot at a US auction — the car in Lviv.”',
  },
  intro: {
    uk:
      'Доставка авто з COPART, IAAI і Manheim в Україну під ключ: викуп лота, море до порту ' +
      'Клайпеда, оформлення та видача у Львові. Працюємо з 2020 року.',
    ru:
      'Доставка авто с COPART, IAAI и Manheim в Украину под ключ: выкуп лота, море до порта ' +
      'Клайпеда, оформление и выдача во Львове. Работаем с 2020 года.',
    en:
      'Turnkey car delivery from COPART, IAAI and Manheim to Ukraine: buying the lot, sea ' +
      'freight to Klaipėda, clearance and handover in Lviv. Working since 2020.',
  },
  footer: {
    uk: '© {year} Larus Logistics · доступ лише для співробітників',
    ru: '© {year} Larus Logistics · доступ только для сотрудников',
    en: '© {year} Larus Logistics · staff access only',
  },
};

/** Профиль по умолчанию: без `BRAND_PROFILE` экземпляр работает как раньше. */
export const DEFAULT_CRM_BRAND = westautoCrmBrand;

export const CRM_BRANDS: Record<string, CrmBrand> = {
  [westautoCrmBrand.id]: westautoCrmBrand,
  [larusCrmBrand.id]: larusCrmBrand,
};

/**
 * Профиль по значению `BRAND_PROFILE`.
 *
 * Опечатка в `.env` не должна ронять вход в систему: отдаём прежний бренд и
 * пишем предупреждение. Падение здесь означало бы, что из-за лишней буквы в
 * переменной никто не может войти в CRM, — цена ошибки несопоставима.
 */
export function resolveCrmBrand(profile: string | null | undefined): CrmBrand {
  const id = profile?.trim();
  if (!id) return DEFAULT_CRM_BRAND;

  const brand = CRM_BRANDS[id];
  if (!brand) {
    console.warn(`[brand] профиль «${id}» неизвестен — беру ${DEFAULT_CRM_BRAND.id}`);
    return DEFAULT_CRM_BRAND;
  }
  return brand;
}

/**
 * Название, разобранное на обычную часть и выделяемый хвост.
 *
 * Хвост, который не является концом имени, — опечатка в профиле. Молча
 * показывать такое имя нельзя (получится «АвтоКлючКлюч» или обрезок),
 * поэтому выделение в этом случае просто не применяется.
 */
export function splitBrandName(brand: CrmBrand): { lead: string; emphasis: string | null } {
  const { name, nameEmphasis } = brand;
  if (!nameEmphasis || !name.endsWith(nameEmphasis) || name === nameEmphasis) {
    return { lead: name, emphasis: null };
  }
  return { lead: name.slice(0, name.length - nameEmphasis.length), emphasis: nameEmphasis };
}

/** Подпись подвала на нужном языке, с подставленным годом. */
export function brandFooter(brand: CrmBrand, year: number, locale: Locale): string {
  return brand.footer[locale].replace('{year}', String(year));
}
